import React, { useEffect, useState } from "react";
import { Modal, Form, Radio, Button, Divider, message } from "antd";
import { useNavigate } from "react-router-dom";
import { SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { DashboardOption, User, EditUserModalProps } from "./UserInterfaces";
import { fetchUserDashboards, updateUser } from "src/services/UserManagementAPIs";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledTextInput from "src/components/reusable/StyledTextInput";

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  user,
  onUpdateUser,
  isSocialListeningEnabled,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const selectedRole = Form.useWatch("role", form);
  const [isSavingChanges, setSavingChanges] = useState<boolean>(false);
  const [dashboardOptions, setDashboardOptions] = useState<DashboardOption[]>([]);
  const [selectedDashboards, setSelectedDashboards] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const navigate = useNavigate();

  interface FormValues {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    status: string;
    dashboardAccess: string[];
    features?: string[];
  }

  useEffect(() => {
    if (isOpen && user) {
      fetchDashboards();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        firstName: user.first_name,
        lastName: user.last_name,
        username: user.name,
        email: user.email,
        role: user.accessLevel.toLowerCase(),
        status: user.isActive ? "active" : "inactive",
        features: user.socialListeningAccess ? ["social listening agent"] : [],
      });
      setSelectedFeatures(user.socialListeningAccess ? ["social listening agent"] : []);
    }
  }, [user, form]);

  const fetchDashboards = async () => {
    if (!user) {
      return;
    }

    try {
      const api_key = localStorage.getItem("api_key") || "";
      const tenant_id = localStorage.getItem("tenant_id") || "";
      const data = await fetchUserDashboards(api_key, tenant_id, user.id);

      const options = data.map(dashboard => ({
        ...dashboard,
        name: dashboard.dashboard_name,
        value: dashboard.dashboard_id.toString(),
        assigned: dashboard.tick_mark,
      }));

      setDashboardOptions(options);

      const selected = options
        .filter(option => option.tick_mark)
        .map(option => option.value);

      setSelectedDashboards(selected);
      form.setFieldsValue({ dashboardAccess: selected });
    } catch (error) {
      if ((error as Error).message === "unauthorized") {
        navigate("/log-in");
      } else {
        console.error("Dashboard fetch error:", error);
        message.error(t("userManagement.modals.editUser.fetchDashboardsFailed"));
      }
    }
  };

  const handleSubmit = async (values: FormValues) => {
    values.dashboardAccess = selectedDashboards;
    setSavingChanges(true);
    const socialListeningAccess = selectedFeatures.includes(
      "social listening agent"
    );

    const updatedUser: User = {
      ...user!,
      name: `${values.firstName} ${values.lastName}`.trim(),
      email: values.email,
      accessLevel: values.role,
      isActive: values.status === "active",
      dashboardAccess: values.dashboardAccess,
      socialListeningAccess,
    };

    try {
      const api_key = localStorage.getItem("api_key") || "";
      const tenant_id = localStorage.getItem("tenant_id") || "";
      const response = await updateUser(
        api_key,
        user?.id!,
        values.firstName,
        values.lastName,
        values.role,
        updatedUser.isActive,
        selectedDashboards.map(id => parseInt(id, 10)),
        socialListeningAccess,
        tenant_id
      );

      if (response.ok) {
        message.success(t("userManagement.modals.editUser.updateSuccess"));
        onUpdateUser(updatedUser);
        form.resetFields();
        setSelectedDashboards([]);
      } else {
        const error = await response.json();
        message.error(
          error.message || t("userManagement.modals.editUser.updateFailedFallback")
        );
      }
    } catch (error) {
      const err = error as any;
      message.error(
        err?.message || t("userManagement.modals.editUser.updateErrorFallback")
      );
    } finally {
      setSavingChanges(false);
    }
  };

  const handleDashboardChange = (value: string[]) => {
    setSelectedDashboards(value);
  };

  return (
    <Modal
      title={t("userManagement.modals.editUser.title")}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      className="add-user-modal"
    >
      <Divider />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="add-user-form"
      >
        <div className="form-row">
          <Form.Item
            name="firstName"
            label={t("userManagement.modals.common.firstNameLabel")}
            className="form-item"
            rules={[
              {
                required: true,
                message: t("userManagement.modals.common.firstNameRequired"),
              },
            ]}
          >
            <StyledTextInput
              placeholder={t("userManagement.modals.common.firstNamePlaceholder")}
            />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={t("userManagement.modals.common.lastNameLabel")}
            className="form-item"
          >
            <StyledTextInput
              placeholder={t("userManagement.modals.common.lastNamePlaceholder")}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="username"
          label={t("userManagement.modals.common.usernameLabel")}
          className="form-item"
          rules={[
            {
              required: true,
              message: t("userManagement.modals.common.usernameRequired"),
            },
          ]}
        >
          <StyledTextInput
            placeholder={t("userManagement.modals.common.usernamePlaceholder")}
          />
        </Form.Item>

        <Form.Item
          name="email"
          label={t("userManagement.modals.common.emailLabel")}
          className="form-item"
        >
          <StyledTextInput
            placeholder={t("userManagement.modals.common.emailPlaceholder")}
            disabled
          />
        </Form.Item>

        <Form.Item
          name="role"
          label={t("userManagement.modals.common.roleLabel")}
          className="form-item"
          rules={[
            {
              required: true,
              message: t("userManagement.modals.common.roleRequired"),
            },
          ]}
        >
          <StyledDropdown
            placeholder={t("userManagement.modals.common.rolePlaceholder")}
            value={selectedRole}
            options={[{ label: t("userManagement.role.viewer"), value: "viewer" }]}
            popupClassName="custom-dropdown-menu"
          />
        </Form.Item>

        <Form.Item
          name="status"
          label={t("userManagement.modals.editUser.statusLabel")}
          className="form-item"
          rules={[
            {
              required: true,
              message: t("userManagement.modals.editUser.statusRequired"),
            },
          ]}
        >
          <Radio.Group>
            <Radio value="active">{t("userManagement.status.active")}</Radio>
            <Radio value="inactive">{t("userManagement.status.inactive")}</Radio>
          </Radio.Group>
        </Form.Item>

        {isSocialListeningEnabled && (
          <Form.Item
            name="features"
            label={t("userManagement.modals.common.featureAccessLabel")}
            className="form-item"
          >
            <StyledDropdown
              mode="multiple"
              placeholder={t("userManagement.modals.common.featurePlaceholder")}
              value={selectedFeatures}
              onChange={value => setSelectedFeatures(value as string[])}
              options={[
                {
                  label: t("userManagement.modals.common.socialListeningAgent"),
                  value: "social listening agent",
                },
              ]}
              showSearch={false}
              maxTagCount={2}
              className="filter-dropdown"
              popupClassName="filter-dropdown-menu"
            />
          </Form.Item>
        )}

        <Form.Item
          name="dashboardAccess"
          label={t("userManagement.modals.common.dashboardAccessLabel")}
          className="form-item"
          rules={[
            {
              required: true,
              message: t("userManagement.modals.common.dashboardRequired"),
            },
          ]}
        >
          <StyledDropdown
            mode="multiple"
            placeholder={t("userManagement.modals.common.dashboardPlaceholder")}
            value={selectedDashboards}
            onChange={value => handleDashboardChange(value as string[])}
            options={dashboardOptions.map(option => ({
              label: option.name,
              value: option.value,
            }))}
            showSearch={false}
            maxTagCount={2}
            className="filter-dropdown"
            popupClassName="filter-dropdown-menu"
          />
        </Form.Item>

        <Form.Item className="form-actions">
          <Button
            type="primary"
            htmlType="submit"
            className="button button--primary"
            disabled={isSavingChanges}
            icon={isSavingChanges ? <SyncOutlined spin /> : null}
          >
            {isSavingChanges
              ? t("userManagement.modals.editUser.savingChanges")
              : t("userManagement.modals.editUser.saveChanges")}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditUserModal;
